import { useState } from "react";
import { Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PersonCard } from "@/components/PersonCard";
import { guestApi, emptyPerson, errMsg, ageOn, splitPhone } from "@/lib/api";

const validate = (persons, fields) => {
  for (const [i, p] of persons.entries()) {
    const n = `Person ${i + 1}`;
    if (!p.first_name?.trim() || !p.last_name?.trim()) return `${n}: please add first and last name`;
    if (p.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p.email)) return `${n}: email format is invalid`;
    if (i === 0 && (!p.email || !p.phone)) return `${n}: email and mobile are required for the main contact`;
    if (p.phone && !/^\d{6,14}$/.test(p.phone.replace(/[\s-]/g, ""))) return `${n}: mobile should have 6–14 digits`;
    const t = p.travel || {};
    if (t.flight_choice === "booked" && t.airline_flight && !/\b(?:[A-Z]{2}|[A-Z]\d|\d[A-Z])\s?-?\d{1,4}[A-Z]?\b/.test(t.airline_flight.toUpperCase())) return `${n}: add the flight number with the airline (e.g. Air India AI 101)`;
    for (const f of fields.filter((x) => x.required && !x.hidden)) {
      const v = f.step === "costume" ? p.costume?.[f.key] : !f.builtin ? p.custom?.[f.key]
        : f.step === "personal" ? p[f.key] : f.key === "flight_choice" ? p.travel?.flight_choice : "skip";
      if (!v) return `${n}: ${f.label} is required`;
    }
  }
  return "";
};

const firstPerson = (family) => ({
  ...emptyPerson(),
  email: family.email || "",
  ...(family.mobile ? splitPhone(family.mobile) : {}),
});

export default function Registration({ family, fields, settings, onSaved }) {
  const [persons, setPersons] = useState(family.persons?.length ? family.persons : [firstPerson(family)]);
  const [busy, setBusy] = useState(false);
  const update = (i, p) => setPersons((ps) => ps.map((x, j) => (j === i ? p : x)));
  const copyPrev = (i) => setPersons((ps) => ps.map((x, j) => {
    if (j !== i) return x;
    const { arrival_date, arrival_time, ...prev } = ps[i - 1].travel || {}; // eslint-disable-line no-unused-vars
    return { ...x, travel: { ...prev, arrival_date: x.travel?.arrival_date, arrival_time: x.travel?.arrival_time } };
  }));
  const children = persons.filter((p) => { const a = ageOn(p.dob, settings.countdown_target); return a !== null && a < 18; }).length;

  const save = async () => {
    const err = validate(persons, fields);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await guestApi.put("/guest/registration", { persons });
      setPersons(data.family.persons);
      onSaved(data.family);
      toast.success("Registration saved. Thank you!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!settings.registrations_open) {
    return <p className="ivory-card p-6 text-slate-600" data-testid="registrations-closed">Registrations are closed. Please contact the organizers for any changes.</p>;
  }

  return (
    <div>
      <div className="space-y-5">
        {persons.map((p, i) => (
          <PersonCard key={p.id} person={p} index={i} fields={fields} refDate={settings.countdown_target}
            onChange={(np) => update(i, np)} onRemove={() => setPersons((ps) => ps.filter((_, j) => j !== i))} onCopyPrev={() => copyPrev(i)} />
        ))}
      </div>
      <button type="button" onClick={() => setPersons((ps) => [...ps, { ...emptyPerson(), last_name: ps[0]?.last_name || "" }])}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-[22px] border border-dashed border-[var(--gold)] py-5 text-[15px] text-[var(--gold-deep)] transition-colors hover:bg-[var(--ivory-2)]" data-testid="add-person-btn">
        <Plus size={18} /> Add another person
      </button>
      <div className="sticky bottom-3 z-20 mt-6 flex items-center justify-between gap-3 rounded-full border border-[var(--line)] bg-white/90 py-2 pl-6 pr-2 shadow-lg backdrop-blur-xl" data-testid="registration-save-bar">
        <span className="text-sm text-slate-600" data-testid="registration-count">{persons.length} {persons.length === 1 ? "person" : "people"}{children ? ` · ${children} child${children > 1 ? "ren" : ""}` : ""}</span>
        <button onClick={save} disabled={busy} className="btn-ink" data-testid="save-registration-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {family.submitted_at ? "Update registration" : "Save registration"}
        </button>
      </div>
    </div>
  );
}
